import { useState } from 'react'

import type { PersonDetail } from '@/data/queries'
import { Card, ProvenanceDate, SeverityPill } from '@/components/bits'
import { supabase } from '@/lib/supabase'
import { longDate, today } from '@/lib/format'

/**
 * The three words HR uses are Active, Terminating and Terminated. The
 * database has three different words, and they are not the same three:
 * `status` is active / pending / left, and it is derived from the dates
 * rather than set by hand. So a lifecycle choice here is really a
 * choice about end_date, and the status follows:
 *
 *   Active       end_date is null
 *   Terminating  end_date is set and still in the future
 *   Terminated   end_date is set and has passed
 *
 * Keeping the mapping in one place stops the two vocabularies leaking
 * into each other.
 */
export type Lifecycle = 'active' | 'terminating' | 'terminated'

export function lifecycleOf(endDate: string | null, now: string): Lifecycle {
  if (endDate === null) return 'active'
  return endDate < now ? 'terminated' : 'terminating'
}

export const LIFECYCLE_LABEL: Record<Lifecycle, string> = {
  active: 'Active',
  terminating: 'Terminating',
  terminated: 'Terminated',
}

const LEAVER_REASONS = [
  'Resignation',
  'End of fixed term',
  'Dismissal',
  'Redundancy',
  'Retirement',
  'Died in service',
]

function Lifecycles({ detail, onSaved }: { detail: PersonDetail; onSaved: () => void }) {
  const now = today()
  const stored = lifecycleOf(detail.employment.end_date, now)

  const [choice, setChoice] = useState<Lifecycle>(stored)
  const [endDate, setEndDate] = useState(detail.employment.end_date ?? '')
  const [reason, setReason] = useState(detail.employment.leaver_reason ?? LEAVER_REASONS[0]!)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  async function save() {
    setBusy(true)
    setError(null)
    const { error: err } = await supabase
      .from('employments')
      .update({
        end_date: choice === 'active' ? null : endDate,
        end_date_source: choice === 'active' ? null : 'hr_confirmed',
        leaver_reason: choice === 'active' ? null : reason,
      })
      .eq('id', detail.employment.id)
    setBusy(false)
    if (err) {
      setError(
        `${err.message}. If this says the row was not found, it is row level security refusing the write rather than a missing record.`,
      )
      return
    }
    setSaved(true)
    onSaved()
  }

  return (
    <>
      <div className="eyebrow" style={{ marginBottom: 9 }}>
        Employment status
      </div>
      <div className="segmented" role="group" aria-label="Employment status">
        {(['active', 'terminating', 'terminated'] as const).map((v) => (
          <button
            key={v}
            type="button"
            data-v={v}
            aria-pressed={choice === v}
            onClick={() => {
              setChoice(v)
              setSaved(false)
              if (v !== 'active' && endDate === '') setEndDate(now)
            }}
          >
            {LIFECYCLE_LABEL[v]}
          </button>
        ))}
      </div>

      {choice !== 'active' && (
        <div className="prompt">
          <h3>
            {choice === 'terminating'
              ? 'When does the employment end?'
              : 'When did the employment end?'}
          </h3>
          <p>
            The last day of employment, not the last day worked. This date is what we write to
            Planday as the deactivation date, and it is what stops shifts being scheduled after it.
          </p>
          <div className="field">
            <label>
              Termination date
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value)
                  setSaved(false)
                }}
              />
            </label>
            <label>
              Reason
              <select value={reason} onChange={(e) => setReason(e.target.value)}>
                {LEAVER_REASONS.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </label>
            <button className="btn" type="button" onClick={() => void save()} disabled={busy || endDate === ''}>
              {busy ? 'Saving…' : `Set ${LIFECYCLE_LABEL[choice].toLowerCase()}`}
            </button>
          </div>

          {error !== null && (
            <div className="callout" style={{ marginTop: 14, borderLeftColor: 'var(--sev-critical)' }}>
              {error}
            </div>
          )}

          {saved && (
            <div className="callout" style={{ marginTop: 14, borderLeftColor: 'var(--sev-overdue)' }}>
              <strong>Recorded here, queued for Planday.</strong> Deactivation on{' '}
              <span className="mono">{endDate}</span> cannot be written yet: Planday exposes no
              endpoint our credentials can reach. The date is saved, the leaver work has opened, and
              the write goes out the moment the endpoint exists. Nothing silently succeeded.
            </div>
          )}
        </div>
      )}

      {choice === 'active' && stored !== 'active' && (
        <div className="callout" style={{ marginTop: 14 }}>
          This employment currently ends on{' '}
          <span className="mono">{detail.employment.end_date}</span>. Saving as Active clears that
          date, which is the right move only if the leaver was recorded in error.
          <div style={{ marginTop: 12 }}>
            <button className="btn sm quiet" type="button" onClick={() => void save()} disabled={busy}>
              Clear the end date
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export function Overview({ person: d, onSaved }: { person: PersonDetail; onSaved: () => void }) {
  const lifecycle = lifecycleOf(d.employment.end_date, today())

  return (
    <div className="cols">
        <div className="stack">
          <div className="card">
            <div className="head">
              <h2>Employment</h2>
              <div className="spacer" />
              <span className={`status ${lifecycle === 'terminated' ? 'terminated' : lifecycle}`}>
                {LIFECYCLE_LABEL[lifecycle]}
              </span>
            </div>
            <div className="body">
              <Lifecycles detail={d} onSaved={onSaved} />

              <div style={{ height: 1, background: 'var(--line-soft)', margin: '22px 0' }} />

              <dl className="dl">
                <dt>Start date</dt>
                <dd>
                  <ProvenanceDate iso={d.employment.start_date} source={d.employment.start_date_source} />
                  {d.employment.start_date_source === 'integration' && (
                    <span className="owner mirror">From Planday</span>
                  )}
                  {d.employment.start_date_source === 'defaulted' && (
                    <span className="owner" style={{ color: 'var(--sev-overdue)' }}>
                      Needs confirming
                    </span>
                  )}
                </dd>

                <dt>Continuous service</dt>
                <dd className="mono">{longDate(d.employment.continuous_service_date)}</dd>

                <dt>Probation ends</dt>
                <dd className="mono">{longDate(d.employment.probation_end_date)}</dd>

                <dt>End date</dt>
                <dd>
                  <ProvenanceDate iso={d.employment.end_date} source={d.employment.end_date_source} />
                </dd>

                <dt>Department</dt>
                <dd>
                  {d.department ?? '—'}
                  <span className="owner mirror">Mirrored</span>
                </dd>

                <dt>Email</dt>
                <dd>{d.person.email ?? '—'}</dd>
              </dl>

              {d.person.compliance_exempt_reason !== null && (
                <div className="callout" style={{ marginTop: 18 }}>
                  <strong>Exempt from compliance reminders.</strong>{' '}
                  {d.person.compliance_exempt_reason} Exempt from a flag is not exempt from the law.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="stack">
          <Card title="Open flags" flush>
            {d.flags.length === 0 ? (
              <div className="empty">
                <p>Nothing open against this employment.</p>
              </div>
            ) : (
              <div className="queue">
                {d.flags.map((f) => (
                  <div className="qrow" key={`${f.ruleCode}:${f.title}`}>
                    <div className="grow">
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span>{f.title}</span>
                        <SeverityPill severity={f.severity} />
                      </div>
                      {f.detail !== null && <small>{f.detail}</small>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title="Still to come">
            <p style={{ margin: 0, fontSize: 13.5, color: 'var(--ivory-dim)' }}>
              The right to work record, the absence balance and the document library. The balance is
              ours to own; the right to work record needs the evidence type, the check date and who
              made it, not just a claim copied out of a spreadsheet.
            </p>
          </Card>
        </div>
      </div>
    )
}
